import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/apiClient';
import '../../css/DiagnosticsView.css';

const PAGE_SIZE = 50;

const DiagnosticsView = () => {
  const [tab, setTab] = useState('requests');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', method: '', ip: '', search: '', dateFrom: '', dateTo: '', anonymousOnly: false });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  useEffect(() => {
    loadLogs();
  }, [tab, page, appliedFilters]);

  const getRequestParams = () => {
    const params = { page, pageSize: PAGE_SIZE };
    if (appliedFilters.method) params.method = appliedFilters.method;
    if (appliedFilters.ip) params.ip = appliedFilters.ip;
    if (appliedFilters.search) params.search = appliedFilters.search;
    if (appliedFilters.dateFrom) params.dateFrom = appliedFilters.dateFrom;
    if (appliedFilters.dateTo) params.dateTo = `${appliedFilters.dateTo}T23:59:59`;
    if (appliedFilters.anonymousOnly) params.anonymousOnly = true;

    if (appliedFilters.status === 'gt200') params.minStatus = 201;
    if (appliedFilters.status === '4xx') { params.minStatus = 400; params.maxStatus = 499; }
    if (appliedFilters.status === '5xx') { params.minStatus = 500; params.maxStatus = 599; }
    if (appliedFilters.status === '429') params.statusCode = 429;
    return params;
  };

  const getErrorParams = () => {
    const params = { page, pageSize: PAGE_SIZE };
    if (appliedFilters.method) params.method = appliedFilters.method;
    if (appliedFilters.search) params.search = appliedFilters.search;
    if (appliedFilters.dateFrom) params.dateFrom = appliedFilters.dateFrom;
    if (appliedFilters.dateTo) params.dateTo = `${appliedFilters.dateTo}T23:59:59`;
    return params;
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = tab === 'requests'
        ? await apiClient.get('/diagnostics/request-audit-logs', { params: getRequestParams() })
        : await apiClient.get('/diagnostics/error-logs', { params: getErrorParams() });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
      setTotalPages(Math.max(response.data.totalPages || 1, 1));
      setExpandedId(null);
    } catch (error) {
      console.error('Nie udało się pobrać logów diagnostycznych.', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const changeTab = (value) => {
    setTab(value);
    setPage(1);
    setExpandedId(null);
  };

  const setQuickStatus = (status) => {
    const next = { ...filters, status };
    setFilters(next);
    setPage(1);
    setAppliedFilters(next);
  };

  const toggleAnonymousOnly = () => {
    const next = { ...filters, anonymousOnly: !filters.anonymousOnly };
    setFilters(next);
    setPage(1);
    setAppliedFilters(next);
  };

  const formatDate = (value) => value ? new Date(value).toLocaleString('pl-PL') : '—';
  const text = (value) => value === null || value === undefined || value === '' ? '—' : String(value);

  const renderRequestDetails = (log) => (
    <div className="diagnostics-details">
      <Detail label="Controller / action" value={`${text(log.controllerName)} / ${text(log.methodName)}`} />
      <Detail label="Forwarded-For" value={log.forwardedFor} />
      <Detail label="Origin" value={log.origin} />
      <Detail label="Referer" value={log.referer} />
      <Detail label="User-Agent" value={log.userAgent} />
      <Detail label="Host / scheme" value={`${text(log.host)} / ${text(log.scheme)}`} />
      <Detail label="Query string" value={log.queryString} />
      <Detail label="Route values" value={log.routeValues} pre />
      <Detail label="Trace identifier" value={log.traceIdentifier} />
      <Detail label="Request body" value={log.requestBody} pre />
      <Detail label="Response body" value={log.responseBody} pre />
    </div>
  );

  const renderErrorDetails = (log) => (
    <div className="diagnostics-details">
      <Detail label="GUID" value={log.guid} />
      <Detail label="Użytkownik" value={log.userId} />
      <Detail label="Kontekst" value={log.contextInfo} pre />
      <Detail label="Payload" value={log.payload} pre />
      <Detail label="Stack trace" value={log.stackTrace} pre />
    </div>
  );

  const previousDisabled = page <= 1 || loading;
  const nextDisabled = page >= totalPages || loading;

  return (
    <div className="diagnostics-page">
      <div className="diagnostics-header">
        <div>
          <h2>Diagnostyka</h2>
          <div className="diagnostics-subtitle">Logi requestów i błędów aplikacji</div>
        </div>
        <div className="diagnostics-tabs">
          <div className={tab === 'requests' ? 'active' : ''} onClick={() => changeTab('requests')}>RequestAuditLogs</div>
          <div className={tab === 'errors' ? 'active' : ''} onClick={() => changeTab('errors')}>ErrorLogs</div>
        </div>
      </div>

      <div className="diagnostics-filters">
        {tab === 'requests' && (
          <div className="diagnostics-quick-filters">
            {[
              ['all', 'Wszystkie'], ['gt200', '> 200'], ['4xx', '4xx'], ['5xx', '5xx'], ['429', '429']
            ].map(([value, label]) => (
              <div key={value} className={filters.status === value ? 'active' : ''} onClick={() => setQuickStatus(value)}>{label}</div>
            ))}
            <div className={filters.anonymousOnly ? 'active' : ''} onClick={toggleAnonymousOnly}>Niezalogowani</div>
          </div>
        )}

        <div className="diagnostics-filter-grid">
          <input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Szukaj w logach..." />
          <select value={filters.method} onChange={e => setFilters({ ...filters, method: e.target.value })}>
            <option value="">Wszystkie metody</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
          {tab === 'requests' && <input value={filters.ip} onChange={e => setFilters({ ...filters, ip: e.target.value })} placeholder="IP" />}
          <input type="date" value={filters.dateFrom} onChange={e => setFilters({ ...filters, dateFrom: e.target.value })} title="Od" />
          <input type="date" value={filters.dateTo} onChange={e => setFilters({ ...filters, dateTo: e.target.value })} title="Do" />
          <div className="diagnostics-apply" onClick={applyFilters}>Zastosuj</div>
        </div>
      </div>

      <div className="diagnostics-summary">Znaleziono: {total}</div>

      <div className="diagnostics-table-wrap">
        <table className="diagnostics-table">
          <thead>
            {tab === 'requests' ? (
              <tr><th>Data</th><th>Status</th><th>Metoda</th><th>Endpoint</th><th>IP</th><th>Użytkownik</th><th>Czas</th></tr>
            ) : (
              <tr><th>Data</th><th>Metoda</th><th>Endpoint</th><th>Wiadomość</th><th>Użytkownik</th></tr>
            )}
          </thead>
          <tbody>
            {!loading && items.length === 0 && <tr><td colSpan={7} className="diagnostics-empty">Brak wyników</td></tr>}
            {items.map(log => (
              <React.Fragment key={log.id}>
                {tab === 'requests' ? (
                  <tr className="diagnostics-row" onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                    <td>{formatDate(log.timestamp)}</td>
                    <td><span className={`status-code status-${Math.floor((log.statusCode || 0) / 100)}xx`}>{text(log.statusCode)}</span></td>
                    <td>{text(log.methodType)}</td>
                    <td className="diagnostics-endpoint">{text(log.url)}</td>
                    <td>{text(log.ipAddress)}</td>
                    <td>{text(log.userName)}</td>
                    <td>{text(log.durationMs)} ms</td>
                  </tr>
                ) : (
                  <tr className="diagnostics-row" onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                    <td>{formatDate(log.timestamp)}</td>
                    <td>{text(log.method)}</td>
                    <td className="diagnostics-endpoint">{text(log.path)}</td>
                    <td>{text(log.message)}</td>
                    <td>{text(log.userId)}</td>
                  </tr>
                )}
                {expandedId === log.id && <tr><td colSpan={7}>{tab === 'requests' ? renderRequestDetails(log) : renderErrorDetails(log)}</td></tr>}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {loading && <div className="diagnostics-loading">Ładowanie...</div>}
      </div>

      <div className="diagnostics-pagination">
        <div className={previousDisabled ? 'disabled' : ''} onClick={() => !previousDisabled && setPage(page - 1)}>Poprzednia</div>
        <span>Strona {page} z {totalPages}</span>
        <div className={nextDisabled ? 'disabled' : ''} onClick={() => !nextDisabled && setPage(page + 1)}>Następna</div>
      </div>
    </div>
  );
};

const Detail = ({ label, value, pre = false }) => (
  <div className="diagnostics-detail">
    <strong>{label}</strong>
    {pre ? <pre>{value === null || value === undefined || value === '' ? '—' : String(value)}</pre> : <div>{value === null || value === undefined || value === '' ? '—' : String(value)}</div>}
  </div>
);

export default DiagnosticsView;
